import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Product } from "../../types/product";
import { fetchItems } from '../queries';
import { RootState } from '../store';

interface ProductsState {
  items: Product[];
  ciscoOnly: Product[];
  ciscoProductMap?: Record<Product['id'], Product>;
  isProductsLoading?: boolean;
  isCiscoProductsLoading?: boolean;
  isProductsInitialized?: boolean;
  isCiscoProductsInitialized?: boolean;
  productError?: string | null;
  ciscoProductsError?: string | null;
  lastFetched?: number;
}

const initialState: ProductsState = {
  items: [],
  ciscoOnly: [],
  isProductsLoading: false,
  isCiscoProductsLoading: false,
  isProductsInitialized: false,
  isCiscoProductsInitialized: false,
  productError: null,
  ciscoProductsError: null,
  ciscoProductMap: {},
  lastFetched: null,
};

export const fetchProducts = createAsyncThunk(
  'products/fetchProducts',
  async () => {
    const data = await fetchItems('db/product', { size: 2000 });
    return data as Product[];
  }
);

export const fetchCiscoProducts = createAsyncThunk(
  'products/fetchCiscoProducts',
  async () => {
    const data = await fetchItems('db/product', { vendor__is_cisco: true, detailed: true });
    return data as Product[];
  }
);

export const checkProductsLoaded = () => async (dispatch, getState) => {
  const state = getState() as RootState;
  if (
     state.products?.items?.length === 0
     // force a refetch daily. in case of an invalitation issue after product changes
     || state.products?.lastFetched && state.products?.lastFetched < Date.now() - 12 * 60 * 60 * 1000
    ) {
    await dispatch(fetchProducts());
  }
};

export const checkCiscoProductsLoaded = () => async (dispatch, getState) => {
  const state = getState() as RootState;
  if (state.products?.ciscoOnly?.length === 0) {
    await dispatch(fetchCiscoProducts());
  }
};


// removing add/update/delete since this is handled via caching, which will trigger reset of the slice state
const productsSlice = createSlice({
  name: 'products',
  initialState,
  reducers: {
    addCiscoProduct(state, action: PayloadAction<Product>) {
      state.items.push(action.payload);
      state.ciscoOnly.push(action.payload);
      state.ciscoProductMap = {
        ...state.ciscoProductMap,
        [action.payload.id]: action.payload,
      };
    },
    updateCiscoProduct(state, action: PayloadAction<Product>) {
      const idxAll = state.items.findIndex(p => p.id === action.payload.id);
      if (idxAll !== -1) {
        state.items[idxAll] = action.payload;
      }

      const idx = state.ciscoOnly.findIndex(p => p.id === action.payload.id);
      if (idx !== -1) {
        state.ciscoOnly[idx] = action.payload;
        state.ciscoProductMap = {
          ...state.ciscoProductMap,
          [action.payload.id]: action.payload,
        };
      }
    },
    deleteCiscoProduct(state, action: PayloadAction<Product['id']>) {
      state.items = state.items.filter(p => p.id !== action.payload);
      state.ciscoOnly = state.ciscoOnly.filter(p => p.id !== action.payload);
      if (state.ciscoProductMap) {
        delete state.ciscoProductMap[action.payload];
      }
    },
    refreshProduct(state, action: PayloadAction<Product>) {
      const idxAll = state.items.findIndex(p => p.id === action.payload.id);
      if (idxAll !== -1) {
        state.items[idxAll] = action.payload;
      }

      const idx = state.ciscoOnly.findIndex(p => p.id === action.payload.id);
      if (idx !== -1) {
        state.ciscoOnly[idx] = action.payload;
        state.ciscoProductMap = {
          ...state.ciscoProductMap,
          [action.payload.id]: action.payload,
        };
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchProducts.pending, (state) => {
        state.isProductsLoading = true;
      })
      .addCase(fetchProducts.fulfilled, (state, action: PayloadAction<Product[]>) => {
        state.items = action.payload;
        state.lastFetched = Date.now();
        state.isProductsLoading = false;
        state.isProductsInitialized = true;
        state.productError = null;
        
      })
      .addCase(fetchProducts.rejected, (state, action) => {
        state.isProductsLoading = false;
        state.productError = action.error.message || 'Failed to load products';
      })

      .addCase(fetchCiscoProducts.pending, (state) => {
        state.isCiscoProductsLoading = true;
      })
      .addCase(fetchCiscoProducts.fulfilled, (state, action: PayloadAction<Product[]>) => {
        state.ciscoOnly = action.payload;
        state.ciscoProductMap = action.payload.reduce((map, product) => {
          map[product.id] = product;
          return map;
        }, {} as Record<Product['id'], Product>);

        state.isCiscoProductsLoading = false;
        state.isCiscoProductsInitialized = true;
        state.ciscoProductsError = null;
      })
      .addCase(fetchCiscoProducts.rejected, (state, action) => {
        state.isCiscoProductsLoading = false;
        state.ciscoProductsError = action.error.message || 'Failed to load Cisco products';
      });
    }
});

export const {
  addCiscoProduct,
  updateCiscoProduct,
  deleteCiscoProduct,
  refreshProduct,
} = productsSlice.actions;
export default productsSlice.reducer;